import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, ChevronUp, ChevronDown, RotateCcw, Save, Loader2, BedDouble } from "lucide-react";

const sectionId = () => Math.random().toString(36).slice(2, 8);

export default function ConfigurazioneChecklistCamera() {
  const [sezioni, setSezioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/room-checklist-template");
      setSezioni((data.sezioni || []).map((s) => ({ ...s, _key: sectionId() })));
      setUpdatedAt(data.updated_at);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveSezione = (idx, dir) => {
    const next = [...sezioni];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setSezioni(next);
  };
  const moveVoce = (sIdx, vIdx, dir) => {
    const next = [...sezioni];
    const voci = [...next[sIdx].voci];
    const newIdx = vIdx + dir;
    if (newIdx < 0 || newIdx >= voci.length) return;
    [voci[vIdx], voci[newIdx]] = [voci[newIdx], voci[vIdx]];
    next[sIdx] = { ...next[sIdx], voci };
    setSezioni(next);
  };
  const updateSezioneNome = (idx, v) => {
    const next = [...sezioni];
    next[idx] = { ...next[idx], nome: v };
    setSezioni(next);
  };
  const updateVoce = (sIdx, vIdx, v) => {
    const next = [...sezioni];
    const voci = [...next[sIdx].voci];
    voci[vIdx] = v;
    next[sIdx] = { ...next[sIdx], voci };
    setSezioni(next);
  };
  const addVoce = (sIdx) => {
    const next = [...sezioni];
    next[sIdx] = { ...next[sIdx], voci: [...next[sIdx].voci, "Nuova voce"] };
    setSezioni(next);
  };
  const removeVoce = (sIdx, vIdx) => {
    const next = [...sezioni];
    next[sIdx] = { ...next[sIdx], voci: next[sIdx].voci.filter((_, i) => i !== vIdx) };
    setSezioni(next);
  };
  const addSezione = () => setSezioni((p) => [...p, { _key: sectionId(), nome: "Nuova sezione", voci: ["Nuova voce"] }]);
  const removeSezione = (idx) => setSezioni((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    // Client validation
    if (!sezioni.length) return toast.error("Almeno una sezione richiesta");
    for (const s of sezioni) {
      if (!s.nome.trim()) return toast.error("Nome sezione vuoto");
      if (!s.voci.length) return toast.error(`Sezione "${s.nome}" senza voci`);
      for (const v of s.voci) if (!String(v).trim()) return toast.error(`Voce vuota in "${s.nome}"`);
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/room-checklist-template", {
        sezioni: sezioni.map((s) => ({ nome: s.nome.trim(), voci: s.voci.map((v) => String(v).trim()) })),
      });
      setUpdatedAt(data.updated_at);
      toast.success("Checklist camera salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    setResetOpen(false);
    setSaving(true);
    try {
      await api.post("/admin/room-checklist-template/reset");
      await load();
      toast.success("Checklist ripristinata ai valori di default");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const totalVoci = sezioni.reduce((s, x) => s + x.voci.length, 0);
  const fmt = updatedAt ? new Date(updatedAt).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Rome" }) : "—";

  return (
    <div className="space-y-5" data-testid="checklist-camera-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display tracking-tight">Checklist Camera</h1>
          <p className="text-sm text-muted-foreground mt-1">Voci compilate per ogni camera durante il Controllo Camere giornaliero · stesso template per tutte le camere.</p>
          <p className="text-[11px] text-muted-foreground mt-1">Ultimo aggiornamento: {fmt} · {sezioni.length} sezioni · {totalVoci} voci</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} disabled={saving} data-testid="reset-default">
            <RotateCcw className="h-4 w-4" /> Ripristina default
          </Button>
          <Button onClick={save} disabled={saving || loading} className="gap-1.5" data-testid="save-checklist">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salva
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {sezioni.map((s, sIdx) => (
          <Card key={s._key || sIdx} className="p-5" data-testid={`section-${sIdx}`}>
            <div className="flex items-start gap-2 mb-4">
              <BedDouble className="h-5 w-5 text-lavender-600 mt-1.5 flex-shrink-0" />
              <Input
                value={s.nome}
                onChange={(e) => updateSezioneNome(sIdx, e.target.value)}
                placeholder="Nome sezione"
                className="text-lg font-display tracking-tight border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b focus-visible:border-lavender-400"
                data-testid={`section-name-${sIdx}`}
              />
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => moveSezione(sIdx, -1)} disabled={sIdx === 0}><ChevronUp className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => moveSezione(sIdx, 1)} disabled={sIdx === sezioni.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => removeSezione(sIdx)} className="text-rose-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="space-y-1.5 pl-7">
              {s.voci.map((v, vIdx) => (
                <div key={vIdx} className="flex items-center gap-2 group">
                  <span className="text-[10px] tabular-nums text-muted-foreground w-6">{vIdx + 1}.</span>
                  <Input
                    value={v}
                    onChange={(e) => updateVoce(sIdx, vIdx, e.target.value)}
                    className="text-sm bg-background/40 border-border/60"
                    data-testid={`voce-input-${sIdx}-${vIdx}`}
                  />
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => moveVoce(sIdx, vIdx, -1)} disabled={vIdx === 0}><ChevronUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => moveVoce(sIdx, vIdx, 1)} disabled={vIdx === s.voci.length - 1}><ChevronDown className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeVoce(sIdx, vIdx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="text-lavender-600 hover:text-lavender-700 mt-1 -ml-2" onClick={() => addVoce(sIdx)} data-testid={`add-voce-${sIdx}`}>
                <Plus className="h-3.5 w-3.5" /> Aggiungi voce
              </Button>
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addSezione} className="w-full border-dashed" data-testid="add-section">
          <Plus className="h-4 w-4" /> Aggiungi sezione
        </Button>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ripristina checklist di default?</DialogTitle>
            <DialogDescription>Tutte le sezioni e voci attuali verranno sostituite con quelle predefinite. Le task camere già aperte mantengono lo snapshot della loro versione.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Annulla</Button>
            <Button onClick={doReset} className="bg-rose-500 hover:bg-rose-600 text-white">Ripristina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-xs text-muted-foreground">Le modifiche diventano effettive per le nuove task. Le task camere già aperte mantengono lo snapshot della checklist al momento della prima compilazione, così non perdi dati a metà compilazione.</p>
    </div>
  );
}
